import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Button, Badge, Table, Modal, Form,
  Spinner, Alert, Tabs, Tab, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator, faCheckCircle, faMoneyBillWave, faTrash,
  faSyncAlt, faInfoCircle, faFileInvoiceDollar, faList,
  faExclamationTriangle, faChevronDown, faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import {
  getLiquidazioniIva, calcolaLiquidazioneIva, aggiornaLiquidazioneIva,
  eliminaLiquidazioneIva, sincronizzaRegistroCassa, getRegistroCassa,
  getRiepilogoCassa,
} from '../../services/api';

const fmt = (v) => `€ ${parseFloat(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('it-IT') : '—';
const ANNO_CORRENTE = new Date().getFullYear();

const STATO_BADGE = {
  bozza:      { bg: 'secondary', label: 'Bozza' },
  confermata: { bg: 'primary',   label: 'Confermata' },
  versata:    { bg: 'success',   label: 'Versata' },
};

const TRIM_LABEL = ['', '1° trim (gen-mar)', '2° trim (apr-giu)', '3° trim (lug-set)', '4° trim (ott-dic)'];

// ─── Modal dettaglio liquidazione ──────────────────────────────────────────

function DettaglioModal({ liq, onClose, onSave }) {
  const [stato, setStato] = useState(liq.stato);
  const [dataVersamento, setDataVersamento] = useState(liq.data_versamento || '');
  const [importoVersato, setImportoVersato] = useState(liq.importo_versato ?? '');
  const [f24, setF24] = useState(liq.f24_protocollo || '');
  const [note, setNote] = useState(liq.note || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await aggiornaLiquidazioneIva(liq.id, {
        stato,
        data_versamento: dataVersamento || null,
        importo_versato: importoVersato !== '' ? parseFloat(importoVersato) : null,
        f24_protocollo: f24 || null,
        note: note || null,
      });
      toast.success('Liquidazione aggiornata');
      onSave();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const saldoColor = liq.saldo > 0 ? '#dc3545' : '#198754';

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2 text-primary" />
          Liquidazione IVA — {TRIM_LABEL[liq.trimestre]} {liq.anno}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col xs={6} md={3}><small className="text-muted d-block">Periodo</small><strong>{fmtDate(liq.data_inizio)} – {fmtDate(liq.data_fine)}</strong></Col>
          <Col xs={6} md={3}><small className="text-muted d-block">Scadenza versamento</small><strong className="text-danger">{fmtDate(liq.data_scadenza_versamento)}</strong></Col>
          <Col xs={6} md={3}><small className="text-muted d-block">Stato</small><Badge bg={STATO_BADGE[liq.stato]?.bg || 'secondary'}>{STATO_BADGE[liq.stato]?.label || liq.stato}</Badge></Col>
          <Col xs={6} md={3}><small className="text-muted d-block">Ultima elaborazione</small><small>{liq.data_calcolo ? new Date(liq.data_calcolo).toLocaleString('it-IT') : '—'}</small></Col>
        </Row>

        <Row className="g-2 mb-4">
          <Col md={6}>
            <Card className="h-100 border-danger">
              <Card.Header className="bg-danger text-white py-2"><strong>IVA a debito</strong></Card.Header>
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between mb-1"><span className="text-muted">Da fatture emesse</span><strong>{fmt(liq.iva_debito_fatture_emesse)}</strong></div>
                <div className="d-flex justify-content-between mb-2"><span className="text-muted">Da corrispettivi</span><strong>{fmt(liq.iva_debito_corrispettivi)}</strong></div>
                <hr className="my-1" />
                <div className="d-flex justify-content-between"><span><strong>Totale debito</strong></span><strong className="text-danger">{fmt(liq.iva_debito_totale)}</strong></div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="h-100 border-success">
              <Card.Header className="bg-success text-white py-2"><strong>IVA a credito</strong></Card.Header>
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between mb-1"><span className="text-muted">Da acquisti</span><strong>{fmt(liq.iva_credito_acquisti)}</strong></div>
                <div className="d-flex justify-content-between mb-2"><span className="text-muted">Credito periodo prec.</span><strong>{fmt(liq.iva_credito_periodo_prec)}</strong></div>
                <hr className="my-1" />
                <div className="d-flex justify-content-between"><span><strong>Totale credito</strong></span><strong className="text-success">{fmt(liq.iva_credito_totale)}</strong></div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4 border-2" style={{ borderColor: liq.saldo > 0 ? '#dc3545' : '#198754' }}>
          <Card.Body className="py-3">
            <Row className="align-items-center">
              <Col>
                {liq.saldo > 0
                  ? <span className="fs-5"><FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" /><strong>IVA da versare:</strong></span>
                  : <span className="fs-5"><FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" /><strong>Credito da riportare:</strong></span>
                }
              </Col>
              <Col xs="auto">
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: saldoColor }}>
                  {liq.saldo > 0 ? fmt(liq.saldo) : fmt(liq.credito_riportato)}
                </span>
              </Col>
            </Row>
            {liq.saldo > 0 && (
              <small className="text-muted d-block mt-1">
                Maggiorazione 1% interessi inclusa nella scadenza (regime trimestrale)
              </small>
            )}
          </Card.Body>
        </Card>

        <hr />
        <h6 className="mb-3">Gestione versamento</h6>
        <Row className="g-2">
          <Col md={3}>
            <Form.Label className="small">Stato</Form.Label>
            <Form.Select size="sm" value={stato} onChange={e => setStato(e.target.value)}>
              <option value="bozza">Bozza</option>
              <option value="confermata">Confermata</option>
              <option value="versata">Versata</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="small">Data versamento</Form.Label>
            <Form.Control size="sm" type="date" value={dataVersamento} onChange={e => setDataVersamento(e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Importo versato (€)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" value={importoVersato} onChange={e => setImportoVersato(e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Protocollo F24</Form.Label>
            <Form.Control size="sm" type="text" value={f24} onChange={e => setF24(e.target.value)} placeholder="es. 2025-0001" />
          </Col>
          <Col md={12}>
            <Form.Label className="small">Note</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={note} onChange={e => setNote(e.target.value)} />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Chiudi</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : null}Salva
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Tab liquidazioni ───────────────────────────────────────────────────────

function TabLiquidazioni() {
  const [anno, setAnno] = useState(ANNO_CORRENTE);
  const [liquidazioni, setLiquidazioni] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calcoloTrim, setCalcoloTrim] = useState(null);  // trimestre in calcolo
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLiquidazioniIva(anno);
      setLiquidazioni(res.data);
    } catch {
      toast.error('Errore nel caricamento liquidazioni');
    } finally {
      setLoading(false);
    }
  }, [anno]);

  useEffect(() => { load(); }, [load]);

  const handleCalcola = async (trimestre) => {
    setCalcoloTrim(trimestre);
    try {
      await calcolaLiquidazioneIva(anno, trimestre);
      toast.success(`Liquidazione ${TRIM_LABEL[trimestre]} ${anno} calcolata`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel calcolo');
    } finally {
      setCalcoloTrim(null);
    }
  };

  const handleElimina = async (liq) => {
    if (!window.confirm(`Eliminare la liquidazione ${TRIM_LABEL[liq.trimestre]} ${liq.anno}?`)) return;
    try {
      await eliminaLiquidazioneIva(liq.id);
      toast.success('Liquidazione eliminata');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Solo le bozze possono essere eliminate');
    }
  };

  // Trimestri già calcolati
  const triCalcolati = new Set(liquidazioni.map(l => l.trimestre));

  // KPI anno
  const totDebito = liquidazioni.reduce((s, l) => s + (l.iva_debito_totale || 0), 0);
  const totCredito = liquidazioni.reduce((s, l) => s + (l.iva_credito_acquisti || 0), 0);
  const totVersato = liquidazioni.filter(l => l.stato === 'versata').reduce((s, l) => s + (l.importo_versato || l.saldo || 0), 0);

  return (
    <>
      {/* Selezione anno + riepilogo */}
      <Row className="align-items-center mb-3">
        <Col xs="auto">
          <Form.Select value={anno} onChange={e => setAnno(+e.target.value)} style={{ width: 100 }}>
            {[ANNO_CORRENTE, ANNO_CORRENTE - 1, ANNO_CORRENTE - 2].map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Form.Select>
        </Col>
        <Col>
          <Row className="g-2">
            <Col xs={6} md={3}>
              <Card className="text-center py-2 border-0 bg-light">
                <div className="small text-muted">Tot. IVA a debito</div>
                <div className="fw-bold text-danger">{fmt(totDebito)}</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="text-center py-2 border-0 bg-light">
                <div className="small text-muted">Tot. IVA a credito</div>
                <div className="fw-bold text-success">{fmt(totCredito)}</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="text-center py-2 border-0 bg-light">
                <div className="small text-muted">Totale versato</div>
                <div className="fw-bold">{fmt(totVersato)}</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="text-center py-2 border-0 bg-light">
                <div className="small text-muted">Liquidazioni</div>
                <div className="fw-bold">{liquidazioni.length}/4</div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && (
        <Table responsive bordered hover size="sm">
          <thead className="table-dark">
            <tr>
              <th>Trimestre</th>
              <th>Periodo</th>
              <th className="text-end">IVA debito</th>
              <th className="text-end">IVA credito</th>
              <th className="text-end">Da versare</th>
              <th>Scadenza</th>
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3, 4].map(t => {
              const liq = liquidazioni.find(l => l.trimestre === t);
              const isCalcolando = calcoloTrim === t;
              return (
                <tr key={t}>
                  <td><strong>{TRIM_LABEL[t]}</strong></td>
                  <td className="small text-muted">
                    {liq ? `${fmtDate(liq.data_inizio)} – ${fmtDate(liq.data_fine)}` : '—'}
                  </td>
                  <td className="text-end text-danger">{liq ? fmt(liq.iva_debito_totale) : '—'}</td>
                  <td className="text-end text-success">{liq ? fmt(liq.iva_credito_totale) : '—'}</td>
                  <td className="text-end fw-bold">
                    {liq
                      ? liq.saldo > 0
                        ? <span className="text-danger">{fmt(liq.saldo)}</span>
                        : <span className="text-success">Credito {fmt(liq.credito_riportato)}</span>
                      : '—'}
                  </td>
                  <td className="small">{liq ? fmtDate(liq.data_scadenza_versamento) : '—'}</td>
                  <td>
                    {liq
                      ? <Badge bg={STATO_BADGE[liq.stato]?.bg || 'secondary'}>{STATO_BADGE[liq.stato]?.label}</Badge>
                      : <Badge bg="light" text="dark">Non calcolata</Badge>}
                  </td>
                  <td>
                    <Button
                      size="sm" variant="outline-primary" className="me-1"
                      onClick={() => handleCalcola(t)}
                      disabled={isCalcolando || (liq && liq.stato !== 'bozza')}
                      title={liq && liq.stato !== 'bozza' ? 'Liquidazione confermata: non ricalcolabile' : 'Calcola'}
                    >
                      {isCalcolando ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faCalculator} />}
                    </Button>
                    {liq && (
                      <>
                        <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setSelected(liq)}>
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </Button>
                        {liq.stato === 'bozza' && (
                          <Button size="sm" variant="outline-danger" onClick={() => handleElimina(liq)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Alert variant="info" className="mt-2 small">
        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
        Regime trimestrale — scadenze: 16 maggio, 16 agosto, 16 novembre, 16 marzo (anno succ.) con maggiorazione 1%.
        IVA su base competenza: la data di riferimento è <em>data emissione</em> per le fatture e <em>data documento</em> per gli acquisti.
      </Alert>

      {selected && (
        <DettaglioModal
          liq={selected}
          onClose={() => setSelected(null)}
          onSave={() => { setSelected(null); load(); }}
        />
      )}
    </>
  );
}

// ─── Tab registro cassa ─────────────────────────────────────────────────────

function TabRegistroCassa() {
  const [anno, setAnno] = useState(ANNO_CORRENTE);
  const [tipo, setTipo] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [riepilogo, setRiepilogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [regRes, rieRes] = await Promise.all([
        getRegistroCassa(anno, tipo || undefined),
        getRiepilogoCassa(anno),
      ]);
      setRows(regRes.data.items);
      setTotal(regRes.data.total);
      setRiepilogo(rieRes.data);
    } catch {
      toast.error('Errore nel caricamento registro');
    } finally {
      setLoading(false);
    }
  }, [anno, tipo]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const res = await sincronizzaRegistroCassa(anno);
      toast.success(`Sincronizzato: ${res.data.incassi} incassi, ${res.data.pagamenti} pagamenti`);
      load();
    } catch {
      toast.error('Errore nella sincronizzazione');
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <>
      <Row className="align-items-center mb-3 g-2">
        <Col xs="auto">
          <Form.Select value={anno} onChange={e => setAnno(+e.target.value)} style={{ width: 100 }}>
            {[ANNO_CORRENTE, ANNO_CORRENTE - 1, ANNO_CORRENTE - 2].map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Form.Select value={tipo} onChange={e => setTipo(e.target.value)} style={{ width: 140 }}>
            <option value="">Tutti</option>
            <option value="incasso">Solo incassi</option>
            <option value="pagamento">Solo pagamenti</option>
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Button variant="outline-primary" size="sm" onClick={handleSync} disabled={syncLoading}>
            {syncLoading ? <Spinner size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSyncAlt} className="me-1" />}
            Sincronizza {anno}
          </Button>
        </Col>
      </Row>

      {riepilogo && (
        <Row className="g-2 mb-3">
          <Col xs={6} md={3}>
            <Card className="text-center py-2 border-0 bg-light">
              <div className="small text-muted">Ricavi incassati (imponibile)</div>
              <div className="fw-bold text-success">{fmt(riepilogo.totale_ricavi_incassati)}</div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="text-center py-2 border-0 bg-light">
              <div className="small text-muted">Costi pagati (imponibile)</div>
              <div className="fw-bold text-danger">{fmt(riepilogo.totale_costi_pagati)}</div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="text-center py-2 border-0 bg-light">
              <div className="small text-muted">Reddito lordo (criterio cassa)</div>
              <div className={`fw-bold ${riepilogo.reddito_lordo_cassa >= 0 ? 'text-success' : 'text-danger'}`}>
                {fmt(riepilogo.reddito_lordo_cassa)}
              </div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="text-center py-2 border-0 bg-light">
              <div className="small text-muted">Movimenti totali</div>
              <div className="fw-bold">{total}</div>
            </Card>
          </Col>
        </Row>
      )}

      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && rows.length === 0 && (
        <Alert variant="warning">
          Nessun movimento per {anno}. Usa "Sincronizza" per generare automaticamente dal DB.
        </Alert>
      )}

      {!loading && rows.length > 0 && (
        <Table responsive bordered hover size="sm">
          <thead className="table-dark">
            <tr>
              <th>Data</th>
              <th>Tipo</th>
              <th>Descrizione</th>
              <th>Origine</th>
              <th className="text-end">Imponibile</th>
              <th className="text-end">IVA</th>
              <th className="text-end">Lordo</th>
              <th>Metodo pag.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td className="small">{fmtDate(r.data_movimento)}</td>
                <td>
                  <Badge bg={r.tipo === 'incasso' ? 'success' : 'danger'}>
                    {r.tipo === 'incasso' ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />}
                    {' '}{r.tipo}
                  </Badge>
                </td>
                <td className="small">{r.descrizione || '—'}</td>
                <td><Badge bg="light" text="dark" className="small">{r.origine}</Badge></td>
                <td className="text-end small">{fmt(r.importo_imponibile)}</td>
                <td className="text-end small">{fmt(r.importo_iva)}</td>
                <td className={`text-end fw-bold ${r.tipo === 'incasso' ? 'text-success' : 'text-danger'}`}>
                  {r.tipo === 'incasso' ? '+' : '-'}{fmt(r.importo_lordo)}
                </td>
                <td className="small">{r.metodo_pagamento || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Alert variant="info" className="mt-2 small">
        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
        Registro incassi/pagamenti art. 18 DPR 600/73 — criterio di <strong>cassa</strong> per il calcolo del reddito imponibile.
        Gli importi IVA qui non sono rilevanti ai fini della liquidazione IVA (che è su competenza).
      </Alert>
    </>
  );
}

// ─── Componente principale ──────────────────────────────────────────────────

export default function LiquidazioneIva() {
  return (
    <div className="container-fluid py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faMoneyBillWave} className="me-2 text-primary" />
            Liquidazione IVA trimestrale
          </h4>
          <small className="text-muted">Regime semplificato — IVA su competenza, reddito su cassa (art. 18 DPR 600/73)</small>
        </Col>
      </Row>

      <Tabs defaultActiveKey="liquidazioni" className="mb-3">
        <Tab eventKey="liquidazioni" title={<><FontAwesomeIcon icon={faFileInvoiceDollar} className="me-1" />Liquidazioni IVA</>}>
          <TabLiquidazioni />
        </Tab>
        <Tab eventKey="cassa" title={<><FontAwesomeIcon icon={faList} className="me-1" />Registro Incassi/Pagamenti</>}>
          <TabRegistroCassa />
        </Tab>
      </Tabs>
    </div>
  );
}
